import { useCallback, useState } from 'react'
import { ImagePlus, X as XIcon } from 'lucide-react'
import {
  X_TWEET_MAX_LENGTH,
  getXTweetWeightedLength,
  truncateXTweetByWeightedLength,
} from '@mondaygpt/constants'
import type { Creative, CreativeType, XPostContent } from '../types'
import { useUpdateCreativeContent } from '../store/draft-creative-context'
import { FieldGroup } from '../components/FieldGroup'
import { ImagePickerModal } from '../components/ImagePickerModal'
import { useEditorHighlight } from '../hooks/useEditorHighlight'

interface XPostEditorProps {
  creative: Creative<CreativeType.XPost>
}

export function XPostEditor({ creative }: XPostEditorProps) {
  const updateContent = useUpdateCreativeContent()
  const { editorRef, highlightedSection, handleEditorBlur, setActiveSection } = useEditorHighlight(
    creative.id
  )
  const content = creative.content
  const [hashtagInput, setHashtagInput] = useState('')
  const [imagePickerOpen, setImagePickerOpen] = useState(false)

  const update = useCallback(
    (field: keyof XPostContent, value: unknown) => {
      updateContent(creative.id, { [field]: value })
    },
    [creative.id, updateContent]
  )

  const addHashtag = (tag: string) => {
    const cleaned = tag.replace(/^#/, '').trim()
    if (cleaned && !content.hashtags.includes(cleaned) && content.hashtags.length < 5) {
      update('hashtags', [...content.hashtags, cleaned])
    }
  }

  const removeHashtag = (tag: string) => {
    update(
      'hashtags',
      content.hashtags.filter(h => h !== tag)
    )
  }

  const handleHashtagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addHashtag(hashtagInput)
      setHashtagInput('')
    }
    if (e.key === 'Backspace' && !hashtagInput && content.hashtags.length > 0) {
      removeHashtag(content.hashtags[content.hashtags.length - 1])
    }
  }

  // X measures tweet length using weighted counting (CJK / emoji = 2,
  // others = 1) — match it so users see the same number X enforces.
  const charCount = getXTweetWeightedLength(content.text)

  return (
    <div ref={editorRef} className="space-y-4" onBlurCapture={handleEditorBlur}>
      {/* Image */}
      <FieldGroup label="Image" sectionId="image" highlighted={highlightedSection === 'image'}>
        <button
          type="button"
          onClick={() => setImagePickerOpen(true)}
          className="group/img relative flex h-40 w-full items-center justify-center rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 transition-colors hover:border-primary/40 hover:bg-gray-100/50"
        >
          {content.imageUrl ? (
            <>
              <img
                src={content.imageUrl}
                alt=""
                className="h-full w-full rounded-lg object-cover"
              />
              <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/0 transition-colors group-hover/img:bg-black/10">
                <span className="rounded-md bg-white/90 px-2.5 py-1 text-[11px] font-medium text-gray-600 opacity-0 shadow-sm transition-opacity group-hover/img:opacity-100">
                  Change image
                </span>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center gap-1.5">
              <ImagePlus size={20} className="text-gray-400" />
              <span className="text-[11px] text-gray-400">Choose image (optional)</span>
              <span className="text-[10px] text-gray-300">Recommended: 1200x675</span>
            </div>
          )}
        </button>
      </FieldGroup>

      {/* Text */}
      <FieldGroup
        label="Text"
        count={`${charCount}/${X_TWEET_MAX_LENGTH}`}
        sectionId="text"
        highlighted={highlightedSection === 'text'}
      >
        <textarea
          value={content.text}
          onChange={e =>
            update('text', truncateXTweetByWeightedLength(e.target.value, X_TWEET_MAX_LENGTH))
          }
          onFocusCapture={() => setActiveSection(creative.id, 'text')}
          placeholder="What's happening?"
          rows={4}
          className={`w-full resize-none rounded-lg border bg-gray-50 px-3 py-2 text-sm transition-colors outline-none placeholder:text-gray-400 focus:border-primary/30 focus:bg-white ${
            charCount > 260 ? 'text-amber-600' : ''
          }`}
        />
      </FieldGroup>

      {/* Hashtags */}
      <FieldGroup
        label={`Hashtags (${content.hashtags.length}/5)`}
        sectionId="hashtags"
        highlighted={highlightedSection === 'hashtags'}
      >
        <div
          onFocusCapture={() => setActiveSection(creative.id, 'hashtags')}
          className="rounded-lg border bg-gray-50 p-2 transition-colors focus-within:border-primary/30 focus-within:bg-white"
        >
          <div className="flex flex-wrap gap-1">
            {content.hashtags.map(tag => (
              <span
                key={tag}
                className="bg-acid-soft flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] font-medium text-acid"
              >
                #{tag}
                <button onClick={() => removeHashtag(tag)} className="text-acid/60 hover:text-acid">
                  <XIcon size={8} />
                </button>
              </span>
            ))}
            {content.hashtags.length < 5 && (
              <input
                type="text"
                value={hashtagInput}
                onChange={e => setHashtagInput(e.target.value)}
                onKeyDown={handleHashtagKeyDown}
                placeholder={content.hashtags.length === 0 ? 'Add hashtags...' : ''}
                className="min-w-[80px] flex-1 bg-transparent py-0.5 text-[11px] outline-none placeholder:text-gray-400"
              />
            )}
          </div>
        </div>
      </FieldGroup>

      {/* Image Picker Modal */}
      <ImagePickerModal
        creativeId={creative.id}
        currentImageUrl={content.imageUrl}
        currentAttachmentId={content.imageAttachmentId}
        open={imagePickerOpen}
        onOpenChange={setImagePickerOpen}
        onSelect={({ imageUrl, imageAttachmentId }) => {
          updateContent(creative.id, { imageUrl, imageAttachmentId })
          setImagePickerOpen(false)
        }}
      />
    </div>
  )
}
